import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service, Timeframe } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { isAlive, redirect } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import ClientGoalsComponent from 'webapp/components/client/goals';
import ClientMetricsItemsComponent from 'webapp/components/client/metrics/items';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import TagContainerComponent from 'webapp/components/tag-container';

import type Space from 'webapp/models-custom/space';

interface Args {
	model: Space;
	canSort: boolean;
	isClientMetrics?: boolean;
	isGoals?: boolean;
	selectedTimeframe: Timeframe | null | undefined;
}

type Action = 'login';

export default class ClientsOverviewRowComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;

	@tracked contextOpened = false;

	@action
	openContext(e: PointerEvent) {
		if (this.contextOpened || this.accountService.handleDemo()) {
			return;
		}

		const attachTo = e.target as HTMLElement,
			popup = this.popupService.createListPopup(attachTo, undefined, 'intervals');

		if (popup) {
			popup.open({
				itemsList: [
					{
						text: 'Log in to account',
						action: 'login',
					},
				],
				doAction: (actionName: Action) => {
					this[actionName]();
				},
			});

			this.contextOpened = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.contextOpened = false;
				}
			});
		}
	}

	@action
	login() {
		redirect(this.args.model.loginUrl);
	}
	<template>
		<div
			data-id={{@model.id}}
			class={{joinClasses
				(array "table-row" "client-row" (if this.contextOpened "contextOpened"))
			}}
		>

			<div class="client">
				{{#if @canSort}}
					<div class="drag"><CommonUiIconComponent @name="ArrowsMove" @size="sm" /></div>
				{{/if}}
				<div class="box">
					<strong>
						<AvatarOrInitialsComponent
							@name={{@model.companyName}}
							@color={{@model.color}}
						/>
						{{@model.companyName}}
					</strong>

					{{#if @model.accountManager}}
						<div class="manager">
							<AvatarOrInitialsComponent
								@name={{@model.accountManager.name}}
								@avatar={{@model.accountManager.avatar}}
							/>
							{{@model.accountManager.name}}
						</div>
					{{/if}}

					<TagContainerComponent
						@model={{@model}}
						@showSingleTagName={{false}}
						@maxShownTags={{5}}
					/>

					<a role="button" class="context" {{on "click" this.openContext}}>...</a>
				</div>
			</div>

			{{#if @isClientMetrics}}
				<ClientMetricsItemsComponent @model={{@model}} />
			{{else if @isGoals}}

				<ClientGoalsComponent @model={{@model}} @selectedTimeframe={{@selectedTimeframe}} />
			{{/if}}

		</div>
	</template>
}
